import {Component, DestroyRef, HostListener, inject} from '@angular/core';
import {SidebarService} from '../../shared/services/sidebar.service';
import {Store} from '@ngxs/store';
import {AppState} from '../../store/app/app.state';
import {combineLatest, from, Observable, of} from 'rxjs';
import {concatMap, distinctUntilChanged, filter, map, skip, startWith, take, toArray} from 'rxjs/operators';
import {AuthenticationService} from '../auth/authentication.service';
import {ConfigurationService} from '../config/configuration.service';
import {EnvironmentEnum} from 'src/app/enums/environment.enum';
import {LanguageService} from 'src/app/core/i18n/language.service';
import {AvailableLang} from '@discover-swiss/models';
import {CheckoutAppService} from 'src/app/shared/services/checkout-core/checkout-app.service';
import {MainPageService} from '../../shared/services/main-page.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CheckoutOrderService} from 'src/app/shared/services/checkout-core/checkout-order.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ScriptsRegistererService} from 'src/app/shared/services/checkout-core/scripts-registerer.service';

@Component({
    standalone: false,
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
    private readonly mainPageService = inject(MainPageService);
    private readonly scriptsRegisterer = inject(ScriptsRegistererService);

    public environmentEnum = EnvironmentEnum;
    public currentEnv = this.config.getCurrentEnv();

    public authorized$: Observable<boolean> = this.store
        .select(AppState.isAuthenticated)
        .pipe(startWith(false));

    public readonly isBookingPage$ = this.mainPageService.isBookingPage$;

    public readonly language$ = this.languageService.languageChange$;
    public readonly languages = this.languageService.availableLang;
    public readonly filteredLanguages$ = combineLatest([
        of(this.languages),
        this.language$
    ]).pipe(
        map(([langs, current]) => langs.filter(lang => lang !== current))
    );
    public showLangDropdown: boolean = false;

    public readonly imageSrc$ = this.language$.pipe(
        distinctUntilChanged(),
        map((lang) => `/assets/images/logo/${lang}-logo.svg`),
    );
    public readonly orderItemCount$ = this.checkoutOrderService.state$.pipe(
        map(state => {
            let num = state.data?.orderedItem?.length ?? 0;
            const nowShowItemsNum = state.data?.orderedItem?.filter(
                (orderItem) => !!orderItem.parentOrderItemNumber
            ).length ?? 0;
            return num - nowShowItemsNum;
        }),
        distinctUntilChanged(),
    );
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private store: Store,
        private authService: AuthenticationService,
        private sidebar: SidebarService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private config: ConfigurationService,
        private languageService: LanguageService,
        private checkoutAppSrv: CheckoutAppService,
        private checkoutOrderService: CheckoutOrderService
    ) {
        const scripts = ['booking-bar', 'checkout-core', 'checkout-shopping-cart'];

        from(scripts)
            .pipe(
                concatMap((script) => this.scriptsRegisterer.register(script)),
                toArray(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.activatedRoute.queryParams.pipe(
            map((queryParams) => queryParams.lang),
            filter((lang) => Boolean(lang)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (lang: AvailableLang) => {
                this.languageService.use(lang as AvailableLang);
            },
        });
    }


    public login(): void {
        this.authService.login();
    }

    public logout(): void {
        this.authService.logout();
    }

    public onMenu(): void {
        this.sidebar.openMenu('menu');
    }

    public onSearch(): void {
        this.sidebar.openMenu('search');
    }

    public selectLang(lang: string): void {
        this.languageService.use(lang as AvailableLang);
        this.switchLangDropdown();
    }

    public switchLangDropdown(): void {
        this.showLangDropdown = !this.showLangDropdown;
    }

    public onHomeClick(): void {
        this.authorized$
            .pipe(skip(1), take(1))
            .subscribe((authorized) => {
                this.checkoutAppSrv.closeCheckout();
                if (authorized) {
                    this.router.navigate(['home']);
                } else if (this.router.url.includes('/booking')) {
                    this.router.navigate(['booking']);
                } else {
                    this.router.navigate(['home']);
                }
            })
    }

    public onShoppingCart() {
        this.router.navigate(['booking']);
        this.checkoutAppSrv.openShoppingCartFromHeader();
    }

    @HostListener('document:click')
    public onDocumentClick(): void {
    }
}
